/**
 * Renders the row prices of a bundle that carries a discount percentage: each row's own price struck
 * through, with its share of the discounted total beside it.
 *
 * Rows are shown in whole dollars while the buy button rounds the total once, so rounding each row on
 * its own would leave the column short of the button. Rows are rounded down instead and the leftover
 * shared out a dollar at a time, biggest remainder first, so the column always matches the button.
 *
 * @param {HTMLElement} container - A `.bundle__product-container` element.
 */
function renderBundleRowPrices(container) {
  if (!container) return;

  const discountPercentage = Number(container.dataset.bundleDiscountPercentage) || 0;
  if (discountPercentage <= 0 || discountPercentage > 100) return;

  const rows = [];
  let totalPrice = 0;

  container.querySelectorAll('card-product-mini').forEach((card) => {
    const priceWrapper = card.querySelector('.card-product-mini__price');
    const selectedColor = card.querySelector('.swatch-group__option input:checked');
    if (!priceWrapper || !selectedColor) return;

    const price = Number(selectedColor.dataset.colorPrice);
    if (!Number.isFinite(price)) return;

    const discountedPrice = price - (price * discountPercentage) / 100;
    const wholeDollars = Math.floor(discountedPrice / 100) * 100;

    rows.push({
      priceWrapper,
      price,
      displayPrice: wholeDollars,
      remainder: discountedPrice - wholeDollars
    });

    totalPrice += price;
  });

  if (rows.length === 0) return;

  // The same total the buy button shows
  const finalPrice = Math.round((totalPrice - (totalPrice * discountPercentage) / 100) / 100) * 100;
  const rowsTotal = rows.reduce((sum, row) => sum + row.displayPrice, 0);
  let leftoverDollars = Math.round((finalPrice - rowsTotal) / 100);

  // Rounding down can only ever undershoot, and by less than a dollar a row, so there is always a row
  // left to give the leftover to.
  rows.sort((a, b) => b.remainder - a.remainder);

  for (const row of rows) {
    if (leftoverDollars <= 0) break;

    row.displayPrice += 100;
    leftoverDollars -= 1;
  }

  for (const row of rows) {
    row.priceWrapper.innerHTML =
      `<s class="price--real-markdown">${formatProductPriceNoZeros(row.price)}</s>` +
      `<span style="color: var(--markdown-price-color);">${formatProductPriceNoZeros(row.displayPrice)}</span>`;
  }
}

class BundleProduct extends HTMLElement{
  constructor() {
    super()

    const parentWrapper = this.closest('.bundle__product-container')
    this.options = this.querySelectorAll('input[type="radio"]');
    this.buyButtonInnerHTML = parentWrapper.querySelector('.product-form__submit > span').innerHTML;
  }

  bindEvents() {
    this.options.forEach((el) => {
      el.addEventListener('change', (e) => {
        this.updatePricingAndBuyButtonState()
      })
    })
  }

  // Updates pricing display and buy button state based on selected options
  updatePricingAndBuyButtonState() {
    const productInfo = this.closest('product-info')
    const buyButton = productInfo.querySelector('bundle-buy-button button')
    const selectedOptions = productInfo.querySelectorAll('bundle-product input[type="radio"]:checked');
    const bundleContainer = this.closest('.bundle__product-container');
    const bundleDiscountPercentage = bundleContainer ? Number(bundleContainer.dataset.bundleDiscountPercentage) || 0 : 0;

    const mainPriceWrapper = document.querySelectorAll('.product__price-reviews > .price');

    let buyButtonActive = true;
    let totalPrice = 0;
    let totalComparePrice = 0;

    selectedOptions.forEach((option) => {
      totalPrice += Number(option.dataset.colorPrice);
      totalComparePrice += option.dataset.colorComparePrice ? Number(option.dataset.colorComparePrice) : Number(option.dataset.colorPrice);
      
      if (option.classList.contains('out-of-stock')) {
        buyButtonActive = false
      }
    })  

    // Apply bundle discount if discount percentage is set
    let finalPrice = totalPrice;
    let finalComparePrice = totalComparePrice;
    
    if (bundleDiscountPercentage > 0 && bundleDiscountPercentage <= 100) {
      // Calculate discount amount
      const discountAmount = (totalPrice * bundleDiscountPercentage) / 100;
      finalPrice = totalPrice - discountAmount;
      // Round to nearest dollar (nearest 100 cents)
      finalPrice = Math.round(finalPrice / 100) * 100;
      // Use original total price as compare price when discount is applied
      finalComparePrice = totalPrice;
    }

    // updating main price
    mainPriceWrapper.forEach((priceWrapper) => {
      // Show sale price if there's a compare price higher than final price (either from discount or original compare price)
      if(finalComparePrice > finalPrice) {
        priceWrapper.classList.add('price--on-sale');
        priceWrapper.querySelector('.price__sale s.price-item--regular').innerHTML = formatProductPriceNoZeros(finalComparePrice);
        priceWrapper.querySelector('.price__sale .price-item--sale').innerHTML = formatProductPriceNoZeros(finalPrice);
      } else {
        priceWrapper.classList.remove('price--on-sale');
        priceWrapper.querySelector('.price__regular .price-item--regular').innerHTML = formatProductPriceNoZeros(finalPrice);
      }
    })

    // Update mobile subtotal display
    const mobileSubtotal = bundleContainer.querySelector('.bundle__subtotal');
    if (mobileSubtotal) {
      if (finalComparePrice > finalPrice) {
        mobileSubtotal.innerHTML = 'Total: ' + formatProductPriceNoZeros(finalPrice) + 
          ' <span>(' + formatProductPriceNoZeros(finalComparePrice) + ' Value)</span>';
      } else {
        mobileSubtotal.innerHTML = 'Total: ' + formatProductPriceNoZeros(finalPrice);
      }
    }

    // Updating bundle buy button
    if (!buyButtonActive) {
      buyButton.setAttribute('disabled', true)
      buyButton.querySelector('span').innerText = 'Sold Out'
    } else {
      buyButton.removeAttribute('disabled')
      buyButton.querySelector('span').innerHTML = this.buyButtonInnerHTML;
      
      // Update buy button price display with discount if applicable
      const buyButtonPriceElement = buyButton.querySelector('.bundle__atc-price');
      if (buyButtonPriceElement) {
        if (finalComparePrice > finalPrice) {
          buyButtonPriceElement.innerHTML = formatProductPriceNoZeros(finalPrice) + 
            ' <span>(' + formatProductPriceNoZeros(finalComparePrice) + ' Value)</span>';
        } else {
          buyButtonPriceElement.innerHTML = formatProductPriceNoZeros(finalPrice);
        }
      }
    }

    renderBundleRowPrices(bundleContainer);
  }

  connectedCallback() {
    this.bindEvents();
    this.updatePricingAndBuyButtonState();
  }
}

customElements.define('bundle-product', BundleProduct)

class BundleBuyButton extends HTMLElement{
  constructor() {
    super();

    this.buyButton = this.querySelector('button')
  }

  connectedCallback() {
    this.buyButton.addEventListener('click', (e) => {
      e.preventDefault();
      const items = []
      const parent = this.closest('.bundle__product-container');
      const selectedItems = parent.querySelectorAll('input[type="radio"]:checked');
      const selectedItemsId = [];

      selectedItems.forEach((item) => selectedItemsId.push(item.value));

      const buttonSpan = this.buyButton.querySelector('span');
      const buttonSpanText = buttonSpan.innerHTML;

      this.buyButton.setAttribute('aria-disabled', true);
      this.buyButton.classList.add('loading');
      this.buyButton.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      // Calculate bundle pricing
      const bundleDiscountPercentage = parent ? Number(parent.dataset.bundleDiscountPercentage) || 0 : 0;
      let totalPrice = 0;
      
      selectedItems.forEach((item) => {
        totalPrice += Number(item.dataset.colorPrice);
      });

      // Calculate final bundle price after discount
      let finalBundlePrice = totalPrice;
      if (bundleDiscountPercentage > 0 && bundleDiscountPercentage <= 100) {
        const discountAmount = (totalPrice * bundleDiscountPercentage) / 100;
        finalBundlePrice = totalPrice - discountAmount;
        // Round to nearest dollar (nearest 100 cents)
        finalBundlePrice = Math.round(finalBundlePrice / 100) * 100;
      }

      selectedItems.forEach((item) => {
        items.push({
          id: item.value,
          quantity: 1,
          properties: {
            _id: new Date().getTime(),
            _bundle_handle: parent.querySelector('[name="bundle__handle"]').value,
            _bundle_id: `${Number(parent.querySelector('[name="bundle__id"]').value)}|${new Date().getTime()}`,
            _bundle_variant_id: Number(parent.querySelector('[name="bundle__id"]').value),
            _bundle_items: selectedItemsId.join('|'),
            _bundle_price: totalPrice,
            _bundle_discount_percentage: bundleDiscountPercentage,
            _bundle_final_price: finalBundlePrice
          }
        })
      });

      window.theme.addToCart(items)
        .then((result) => {
          if (result.error) {
            this.buyButton.setAttribute('aria-disabled', true);
            this.buyButton.querySelector('span').classList.add('hidden');
            return
          }
        }).finally(() => {
          this.buyButton.classList.remove('loading');
          if (!this.error) this.buyButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');

          buttonSpan.innerHTML = 'Added!';
          setTimeout(function(){
            buttonSpan.innerHTML = buttonSpanText;
          }, 3000);
        });
    })
  }
}

customElements.define('bundle-buy-button', BundleBuyButton)

// Hide yotpo stars botomline if there are no reviews
function onElementAdded(selector, callback) {
  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        const element = document.querySelector(selector);
        if (element) {
          callback(element);
          observer.disconnect(); // Stop observing once the element is found
        }
      }
    }
  });

  // Start observing the entire document
  observer.observe(document.querySelector('.main-product-page-width'), { childList: true, subtree: true });
}

// Usage example: Check for an element with the ID #new-element
onElementAdded('.yotpo.bottomLine .yotpo-stars', (element) => {
  if(element.querySelectorAll('span.yotpo-icon-empty-star').length === 5) {
    document.querySelectorAll('.yotpo.bottomLine').forEach((el) => el.classList.add('hidden'))
  }
});